var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { post } from "../../http.mjs";
import { assertUnreachable } from "../../utils.mjs";
import { logger } from "../../log.mjs";
import { sendCompletePurchase } from "../../completePurchase.mjs";
import { paymentMethodsWithOwnBuyButton } from "../../cart/payment.mjs";
import { updateAdyenPaymentSessionResponse } from "./schema.mjs";
// We need to keep track of the current order id because we might initiate a payment
// and then the customer might need to complete more steps such as 3D Secure or other
// additional verification steps. In that case we need to know which order id to use
// when we receive the onAdditionalDetails callback from Adyen.
let currentOrderId;
// This function is called when the Adyen payment method is selected in the checkout
// and is responsible for initializing the Adyen Drop-in component and handling the
// submission of the payment data.
export const initializeAdyenPaymentMethod = (checkoutState, checkoutActions, paymentMethod, element) => __awaiter(void 0, void 0, void 0, function* () {
    if (!checkoutState.checkout) {
        throw new Error('state.checkout is not loaded');
    }
    let resolveProviderDataPromise;
    let providerDataPromise;
    let providerData;
    const setPaymentData = (data) => {
        if (data.isValid) {
            providerData = data.data;
        }
        else {
            providerData = undefined;
        }
        if (resolveProviderDataPromise) {
            resolveProviderDataPromise(providerData);
        }
    };
    const configuration = Object.assign(Object.assign({}, sharedAdyenConfiguration(checkoutState, paymentMethod.environment, paymentMethod.clientKey)), { paymentMethodsResponse: {
            paymentMethods: paymentMethod.adyenPaymentMethods,
            storedPaymentMethods: [],
        }, onChange: setPaymentData, onSubmit: (data, _component, actions) => __awaiter(void 0, void 0, void 0, function* () {
            var _a, _b, _c;
            const proceedWithPurchase = (_b = (_a = checkoutState.onBeforePurchase) === null || _a === void 0 ? void 0 : _a.call(checkoutState)) !== null && _b !== void 0 ? _b : true;
            if (!proceedWithPurchase) {
                actions.reject();
                setDropinError('', dropinInstance, checkoutState);
                return;
            }
            if (paymentMethodsWithOwnBuyButton.adyen.includes(paymentMethod.id)) {
                if (checkoutState.completePurchaseReadyState !== 'ready') {
                    actions.reject();
                    setDropinError('', dropinInstance, checkoutState);
                    return;
                }
                checkoutState.update({ paymentInProgressWithPaymentMethodId: paymentMethod.id });
                const recaptchaToken = (_c = (yield checkoutActions.getRecaptchaToken())) !== null && _c !== void 0 ? _c : null;
                const response = yield sendCompletePurchase(checkoutState, 'adyen', data.data, recaptchaToken);
                return handleCompletePurchaseResponse({ checkoutState, checkoutActions, response, dropinInstance, element });
            }
            else {
                setPaymentData(data);
            }
        }), onAdditionalDetails: (state, _component, actions) => __awaiter(void 0, void 0, void 0, function* () {
            if (!currentOrderId) {
                throw new Error('Implementation error, no order id has been set which is needed to handle onAdditionalDetails');
            }
            const body = {
                paymentData: state.data.paymentData,
                details: state.data.details,
            };
            try {
                const response = yield post(`/adyen/payments/${currentOrderId}/details`, checkoutState.checkoutApiBaseUrl, body, updateAdyenPaymentSessionResponse, checkoutState.token);
                if (!response.success) {
                    setPaymentVerificationError(checkoutState, response.errorMessage || 'Payment failed');
                    actions.reject();
                }
                else {
                    checkoutActions.clearCartAndShowOrderConfirmation(currentOrderId);
                    actions.resolve(response.paymentsDetailsResponse);
                }
            }
            catch (err) {
                logger.error({ err }, 'An error occured in onAdditionalDetails');
                checkoutState.onUnexpectedError('An unexpected error occurred. Please contact customer service.');
                actions.reject();
            }
        }), onError: (e) => {
            console.error(e);
            providerData = undefined;
        }, showPayButton: paymentMethodsWithOwnBuyButton.adyen.includes(paymentMethod.id), amount: {
            currency: checkoutState.currency,
            value: checkoutState.checkout.leftToPay.minor.amountInclTax,
        } });
    const adyen = yield adyenInstance(configuration);
    logger.info({ context: adyen.loadingContext }, 'Adyen loading context');
    const Dropin = (yield lazyImportAdyen()).Dropin;
    yield lazyImportAdyenStyles();
    const dropinInstance = new Dropin(adyen, {
        openFirstPaymentMethod: true,
        paymentMethodsConfiguration: paymentMethod.adyenPaymentMethodConfigurations,
    });
    dropinInstance.mount(element);
    const removeCompletePurchaseHandler = checkoutActions.setCompletePurchaseHandler(() => __awaiter(void 0, void 0, void 0, function* () {
        checkoutState.update({ paymentInProgressWithPaymentMethodId: paymentMethod.id });
        providerData = undefined;
        providerDataPromise = new Promise((resolve) => (resolveProviderDataPromise = resolve));
        dropinInstance.submit();
        if (providerData) {
            resolveProviderDataPromise(providerData);
        }
        const providerDataToSend = yield providerDataPromise;
        if (providerDataToSend) {
            const response = yield sendCompletePurchase(checkoutState, 'adyen', providerDataToSend, yield checkoutActions.getRecaptchaToken());
            return handleCompletePurchaseResponse({ checkoutState, checkoutActions, response, dropinInstance, element });
        }
        else {
            // We intentionally don't return an error message here because in this case
            // Adyen will have called our onError callback which will have displayed an error
            // so we don't need to display another one.
            const result = { $type: 'validationError', errors: [] };
            return result;
        }
    }));
    const toggleDropinInteractivity = (newState) => {
        // When we don't control the buy button we need to disable the dropin instance
        // until we're ready to complete the purchase.
        if (paymentMethodsWithOwnBuyButton.adyen.includes(paymentMethod.id)) {
            if (newState.completePurchaseReadyState !== 'ready') {
                element.style.pointerEvents = 'none';
            }
            else {
                element.style.pointerEvents = '';
            }
        }
    };
    toggleDropinInteractivity(checkoutState);
    const disconnectReadyStateListener = checkoutState.onChange(toggleDropinInteractivity);
    return () => {
        removeCompletePurchaseHandler();
        disconnectReadyStateListener();
        try {
            dropinInstance.unmount();
        }
        catch (_) {
            // Ignore errors when unmounting the Adyen Drop-in component
            // because it might have already gotten unmounted by the user
            // navigating away from the checkout. When the user comes back
            // to the checkout and we initialize a payment method again
            // we'll try to unmount but at that point it's no longer mounted.
        }
    };
});
const handleCompletePurchaseResponse = (_a) => __awaiter(void 0, [_a], void 0, function* ({ checkoutState, checkoutActions, response, dropinInstance, element, }) {
    const $type = response.$type;
    switch ($type) {
        case 'success':
        case 'requiresClientProcessing':
        case 'redirect':
        case 'postForm': {
            if ($type === 'requiresClientProcessing') {
                currentOrderId = response.orderId;
                checkoutState.onElementFocus(element);
            }
            setupPaymentVerificationListener(checkoutState, dropinInstance);
            yield handleSuccessfulCompletePurchase(checkoutState, checkoutActions, response, dropinInstance);
            // handleSuccessfulCompletePurchase() will handle all these cases by either returning a never-resolving promise
            // or calling the appropriate callback on the state object, so it's safe to return noAction here.
            return { $type: 'noAction' };
        }
        case 'failed':
            return handleFailedCompletePurchase(response, dropinInstance, checkoutState);
        case 'unexpectedError':
            setDropinError('An unexpected error occurred. Please contact customer service.', dropinInstance, checkoutState);
            return response;
        default:
            assertUnreachable($type);
    }
    // We'll never get here, but TypeScript doesn't know that for some reason
    return { $type: 'unexpectedError' };
});
const handleSuccessfulCompletePurchase = (checkoutState, checkoutActions, completePurchaseResponse, dropinInstance) => __awaiter(void 0, void 0, void 0, function* () {
    if (completePurchaseResponse.$type === 'success') {
        yield checkoutActions.clearCartAndShowOrderConfirmation(completePurchaseResponse.orderId);
    }
    else if (completePurchaseResponse.$type === 'requiresClientProcessing') {
        handleRequiresClientProcessing(completePurchaseResponse, dropinInstance);
    }
    else if (completePurchaseResponse.$type === 'redirect') {
        checkoutState.onRedirect(completePurchaseResponse.url);
        // Return never-resolving promise to prevent the checkout from continuing
        return new Promise(() => { });
    }
    else if (completePurchaseResponse.$type === 'postForm') {
        checkoutState.onPostForm(completePurchaseResponse.url, completePurchaseResponse.data, completePurchaseResponse.formEncoding);
        // Return never-resolving promise to prevent the checkout from continuing
        return new Promise(() => { });
    }
    else {
        assertUnreachable(completePurchaseResponse);
    }
});
// This handles cases where additional action is required from the client to complete the payment
// such as 3D Secure or other additional verification steps.
const handleRequiresClientProcessing = (completePurchaseResponse, dropinInstance) => __awaiter(void 0, void 0, void 0, function* () {
    const adyenClientData = completePurchaseResponse.clientData;
    if (adyenClientData.provider !== 'adyen') {
        throw new Error(`Unsupported payment provider: ${adyenClientData.provider}`);
    }
    const paymentAction = completePurchaseResponse.clientData.action;
    dropinInstance.handleAction(paymentAction);
});
const handleFailedCompletePurchase = (response, dropinInstance, checkoutState) => {
    const paymentErrors = response.errors.filter((e) => e.type === 'paymentError');
    if (paymentErrors.length) {
        setDropinError(paymentErrors
            .map((e) => e.message)
            .filter((e) => !!e)
            .join(', ') || 'An unexpected error occurred. Please contact customer service.', dropinInstance, checkoutState);
    }
    else {
        // The error wasn't a payment error so we enable the payment method again
        checkoutState.update({ paymentInProgressWithPaymentMethodId: undefined });
        dropinInstance.setStatus('ready');
    }
    return response;
};
// This sets up a state listener to check if the payment verification state has failed
// and if it does we display the error message inside the Adyen Drop-in component instead
// of having to show the error inside the verification modal.
const setupPaymentVerificationListener = (checkoutState, dropinInstance) => {
    const unsubscribe = checkoutState.onChange((newState, oldState) => __awaiter(void 0, void 0, void 0, function* () {
        if (!newState.checkoutElements.paymentVerification && oldState.checkoutElements.paymentVerification) {
            unsubscribe();
            return;
        }
        if (newState.id !== oldState.id) {
            unsubscribe();
            return;
        }
        const paymentVerification = newState.checkoutElements.paymentVerification;
        if ((paymentVerification === null || paymentVerification === void 0 ? void 0 : paymentVerification.state) === 'error') {
            setDropinError(paymentVerification.errorMessage, dropinInstance, checkoutState);
            unsubscribe();
            checkoutState.update({
                checkoutElements: Object.assign(Object.assign({}, checkoutState.checkoutElements), { paymentVerification: undefined }),
            });
        }
    }));
};
const setPaymentVerificationError = (checkoutState, errorMessage) => {
    checkoutState.update({
        checkoutElements: Object.assign(Object.assign({}, checkoutState.checkoutElements), { paymentVerification: {
                elementId: 'payment-verification-container',
                state: 'error',
                errorMessage,
            } }),
    });
};
const setDropinError = (message, dropinInstance, checkoutState) => {
    dropinInstance.setStatus('error', {
        message,
    });
    return new Promise((resolve) => setTimeout(() => {
        dropinInstance.setStatus('ready');
        checkoutState.update({ paymentInProgressWithPaymentMethodId: undefined });
        resolve();
    }, 3000));
};
const lazyImportAdyen = () => __awaiter(void 0, void 0, void 0, function* () {
    const importedAdyen = yield import('@adyen/adyen-web/auto');
    // This isn't great but if we don't do this tomfoolery we get type errors in checkout-api because it uses a different module
    // resolution strategy than the SDK.
    const defaultExportAsAdyen = importedAdyen.default;
    const nonDefaultExportAsAdyen = importedAdyen;
    if (defaultExportAsAdyen && !!defaultExportAsAdyen.AdyenCheckout) {
        return defaultExportAsAdyen;
    }
    else if (nonDefaultExportAsAdyen && !!nonDefaultExportAsAdyen.AdyenCheckout) {
        return nonDefaultExportAsAdyen;
    }
    else {
        throw new Error('Adyen SDK could not be loaded correctly, the import contained the following keys: ' + Object.keys(importedAdyen).join(', '));
    }
});
const lazyImportAdyenStyles = () => __awaiter(void 0, void 0, void 0, function* () {
    yield import('@adyen/adyen-web/styles/adyen.css');
});
const adyenInstance = (configuration) => __awaiter(void 0, void 0, void 0, function* () {
    return yield (yield lazyImportAdyen()).AdyenCheckout(configuration);
});
const sharedAdyenConfiguration = (checkoutState, environment, clientKey) => {
    let locale = checkoutState.language;
    if (!locale.includes('-')) {
        locale = `${locale}-${checkoutState.countryCode.toUpperCase()}`;
    }
    const configuration = {
        locale,
        countryCode: checkoutState.countryCode.toUpperCase(),
        environment: environment,
        clientKey: clientKey,
        loadingContext: checkoutState.siteUrl,
    };
    return configuration;
};
//# sourceMappingURL=client.js.map